// DiagiaResults — Phase 4 admin analytics across all campaigns.
//
// Layout (top to bottom):
//   1. Page head with title + "Exporter PDF" (browser print)
//   2. KPI strip (Total diagnostics, Score moyen, Campagnes, Cas notés)
//   3. Two-column row: Score distribution chart  |  Top use cases table
//   4. Tag frequency cloud
//   5. Per-campaign breakdown table
//
// All numbers come from /api/diagia/results. Empty states stay graceful so
// the page is shippable even before any diagnostic has been generated.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  FaPrint,
  FaCheckDouble,
  FaStar,
  FaBullhorn,
  FaCertificate,
} from 'react-icons/fa';

import useDiagiaApi from '../hooks/useDiagiaApi';
import KpiCard from '../components/KpiCard';

const BUCKET_ORDER = ['0-49', '50-64', '65-79', '80-100'];
const BUCKET_LABEL = {
  '0-49':   'À écarter',
  '50-64':  'À explorer',
  '65-79':  'À cadrer',
  '80-100': 'Prioritaire',
};

const VERDICT_TONE = {
  'Prioritaire':  'is-active',
  'À cadrer':     'is-draft',
  'À explorer':   'is-draft',
  'À écarter':    'is-archived',
};

export default function DiagiaResults() {
  const { t } = useTranslation();
  const api = useDiagiaApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getResults()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.error || 'Erreur de chargement');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api]);

  const kpis = data?.kpis || {};
  const buckets = data?.buckets || {};
  const topCases = data?.top_cases || [];
  const tags = data?.tags || [];
  const perCampaign = data?.per_campaign || [];

  const maxBucket = Math.max(1, ...BUCKET_ORDER.map((b) => buckets[b] || 0));
  const maxTag = Math.max(1, ...tags.map((t) => t.count || 0));

  return (
    <div className="dia-page dia-results">
      <header className="dia-page-head dia-no-print">
        <div>
          <span className="dia-section-eyebrow">{t('diagia.nav.admin_section', 'Administration')}</span>
          <h1>{t('diagia.nav.results', 'Résultats')}</h1>
          <p>{t('diagia.res.sub', "Agrégation des diagnostics et des cas d'usage identifiés.")}</p>
        </div>
        <button
          type="button"
          className="dia-cta dia-cta-ghost"
          onClick={() => window.print()}
        >
          <FaPrint /> Exporter en PDF
        </button>
      </header>

      {loading && <p className="dia-empty">{t('diagia.home.loading', 'Chargement…')}</p>}
      {!loading && error && <p className="dia-error">{error}</p>}

      {!loading && !error && (
        <>
          <div className="dia-kpis">
            <KpiCard
              icon={<FaCheckDouble />}
              tone="mist"
              label="Diagnostics complétés"
              value={kpis.diagnostics_total ?? 0}
            />
            <KpiCard
              icon={<FaStar />}
              tone="amber"
              label="Score moyen"
              value={kpis.avg_score !== null && kpis.avg_score !== undefined ? `${kpis.avg_score}/100` : '—'}
            />
            <KpiCard
              icon={<FaBullhorn />}
              tone="sky"
              label="Campagnes"
              value={kpis.campaigns_total ?? 0}
            />
            <KpiCard
              icon={<FaCertificate />}
              tone="lilac"
              label="Cas notés"
              value={kpis.scored_total ?? 0}
            />
          </div>

          <div className="dia-results-grid">
            {/* Score distribution */}
            <div className="dia-card">
              <header className="dia-card-head">
                <h3>{t('diagia.res.distribution', 'Distribution des scores')}</h3>
                <span className="dia-card-sub">
                  Par tranche de note globale
                </span>
              </header>
              {(kpis.scored_total ?? 0) === 0 ? (
                <p className="dia-empty">{t('diagia.res.no_scored', "Aucun cas noté pour l'instant.")}</p>
              ) : (
                <ul className="dia-bars">
                  {BUCKET_ORDER.map((b) => {
                    const v = buckets[b] || 0;
                    const w = Math.round((v / maxBucket) * 100);
                    return (
                      <li key={b}>
                        <span className="dia-bars-label">{BUCKET_LABEL[b]}</span>
                        <span className="dia-bars-range">{b}</span>
                        <span className="dia-bars-track">
                          <span style={{ width: `${w}%` }} />
                        </span>
                        <span className="dia-bars-count">{v}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Top use cases */}
            <div className="dia-card">
              <header className="dia-card-head">
                <h3>{t('diagia.res.priority', "Cas d'usage prioritaires")}</h3>
                <span className="dia-card-sub">
                  Top {topCases.length || '—'} par score
                </span>
              </header>
              {topCases.length === 0 ? (
                <p className="dia-empty">{t('diagia.res.no_scored', "Aucun cas noté pour l'instant.")}</p>
              ) : (
                <table className="dia-table">
                  <thead>
                    <tr>
                      <th>{t('diagia.home.th_title', 'Titre')}</th>
                      <th>{t('diagia.home.th_score', 'Score')}</th>
                      <th>{t('diagia.res.th_verdict', 'Verdict')}</th>
                      <th>{t('diagia.part.th_campaign', 'Campagne')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topCases.map((d) => (
                      <tr key={d.id}>
                        <td className="dia-table-title">
                          <Link to={`/diagia/diagnostics/${d.id}`}>{d.title || 'Sans titre'}</Link>
                        </td>
                        <td>
                          <span className="dia-score-pill">{d.score}/100</span>
                        </td>
                        <td>
                          <span className={`dia-status ${VERDICT_TONE[d.verdict] || 'is-active'}`}>
                            {d.verdict}
                          </span>
                        </td>
                        <td>
                          {d.campaign_id ? (
                            <Link to={`/diagia/campaigns/${d.campaign_id}`}>
                              {d.campaign_name || '—'}
                            </Link>
                          ) : (
                            <span className="dia-table-muted">{t('diagia.res.no_campaign', 'Hors campagne')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Tag cloud */}
          <div className="dia-card">
            <header className="dia-card-head">
              <h3>{t('diagia.res.themes', 'Thèmes récurrents')}</h3>
              <span className="dia-card-sub">
                Tags extraits des diagnostics ({tags.length} thèmes)
              </span>
            </header>
            {tags.length === 0 ? (
              <p className="dia-empty">{t('diagia.res.no_themes', 'Pas encore de thèmes identifiés.')}</p>
            ) : (
              <ul className="dia-tag-cloud">
                {tags.map((t) => {
                  // Scale 0.85rem … 1.4rem based on count
                  const ratio = t.count / maxTag;
                  const size = 0.85 + ratio * 0.55;
                  return (
                    <li
                      key={t.tag}
                      style={{ fontSize: `${size}rem` }}
                      className={ratio > 0.6 ? 'dia-tag-hot' : ''}
                    >
                      <span>#{t.tag}</span>
                      <small>{t.count}</small>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Per-campaign breakdown */}
          <div className="dia-card">
            <header className="dia-card-head">
              <h3>{t('diagia.res.by_campaign', 'Par campagne')}</h3>
              <span className="dia-card-sub">
                {perCampaign.length} campagne{perCampaign.length > 1 ? 's' : ''}
              </span>
            </header>
            {perCampaign.length === 0 ? (
              <p className="dia-empty">{t('diagia.res.no_campaigns', "Vous n'avez pas encore créé de campagne.")}</p>
            ) : (
              <table className="dia-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>{t('diagia.nav.participants', 'Participants')}</th>
                    <th>{t('diagia.res.th_diags', 'Diagnostics')}</th>
                    <th>{t('diagia.res.th_completion', 'Complétion')}</th>
                    <th>{t('diagia.res.th_avg', 'Score moyen')}</th>
                  </tr>
                </thead>
                <tbody>
                  {perCampaign.map((c) => {
                    const pct = c.participants_count
                      ? Math.round((c.completed_count / c.participants_count) * 100)
                      : 0;
                    return (
                      <tr key={c.id}>
                        <td className="dia-table-title">
                          <Link to={`/diagia/campaigns/${c.id}`}>{c.name}</Link>
                          {c.scope && (
                            <div className="dia-table-muted" style={{ fontSize: '0.78rem' }}>
                              {c.scope}
                            </div>
                          )}
                        </td>
                        <td>{c.participants_count}</td>
                        <td>{c.diagnostics_count}</td>
                        <td>
                          <div className="dia-progress-cell">
                            <div className="dia-progress-track">
                              <span style={{ width: `${pct}%` }} />
                            </div>
                            <span className="dia-progress-pct">{pct}%</span>
                          </div>
                        </td>
                        <td>
                          {c.avg_score !== null && c.avg_score !== undefined ? (
                            <span className="dia-score-pill">{c.avg_score}/100</span>
                          ) : (
                            <span className="dia-table-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
